import { useRef } from "react";
import {
  ArrowUpRight,
  Check,
  ClipboardList,
  Compass,
  HardHat,
  KeyRound,
  MoveRight,
} from "lucide-react";
import { motion, useInView } from "framer-motion";

const steps = [
  {
    step: "01",
    title: "Understand",
    eyebrow: "Discover your needs",
    description:
      "We begin by understanding your vision, lifestyle, functional needs, site conditions, and budget before shaping the project.",
    points: [
      "Requirement discussion",
      "Site review and measurements",
      "Budget and schedule planning",
    ],
    Icon: ClipboardList,
    footer: "Clarity before work begins",
  },
  {
    step: "02",
    title: "Plan & Design",
    eyebrow: "Create the right solution",
    description:
      "We develop a practical direction for your space, bringing together layout, materials, finishes, and design preferences.",
    points: [
      "Space planning and layouts",
      "Design concepts and visualisation",
      "Material and finish decisions",
    ],
    Icon: Compass,
    footer: "Every detail has a purpose",
  },
  {
    step: "03",
    title: "Build & Finish",
    eyebrow: "Bring the plan to life",
    description:
      "From construction and interiors to painting and finishing, the work is coordinated around the agreed project scope.",
    points: [
      "Organised work execution",
      "Progress and site coordination",
      "Quality checks at key stages",
    ],
    Icon: HardHat,
    footer: "Care throughout execution",
  },
  {
    step: "04",
    title: "Review & Handover",
    eyebrow: "Complete your space",
    description:
      "We review the completed work with you, address agreed finishing items, and prepare the project for handover.",
    points: [
      "Final walkthrough",
      "Review of finishing details",
      "Handover of completed work",
    ],
    Icon: KeyRound,
    footer: "The final details matter",
  },
];

function ProcessCard({ step, index, sectionInView }) {
  const Icon = step.Icon;

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      animate={sectionInView ? { opacity: 1, y: 0 } : {}}
      transition={{
        duration: 0.55,
        delay: index * 0.12,
        ease: "easeOut",
      }}
      className="group relative flex min-w-0 flex-col border-t border-slate-200 bg-white px-5 py-7 transition-colors duration-300 hover:border-indigo-500 sm:px-7 sm:py-8 md:px-6 lg:px-5 lg:py-8 xl:px-8 xl:py-10"
    >
      {/* Step header */}
      <div className="flex items-start justify-between gap-4">
        <span className="font-['Poppins',sans-serif] text-5xl font-semibold leading-none tracking-tight text-slate-200 transition-colors duration-300 group-hover:text-indigo-200 sm:text-6xl xl:text-7xl">
          {step.step}
        </span>

        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-50 text-slate-700 transition-all duration-300 group-hover:bg-indigo-600 group-hover:text-white sm:h-14 sm:w-14">
          <Icon
            className="h-5 w-5 sm:h-6 sm:w-6"
            strokeWidth={1.5}
            aria-hidden="true"
          />
        </div>
      </div>

      {/* Card copy */}
      <div className="mt-7 sm:mt-9">
        <p className="font-['DM_Sans',sans-serif] text-[10px] font-bold uppercase tracking-[0.17em] text-indigo-600 sm:text-xs">
          {step.eyebrow}
        </p>

        <h3 className="mt-2 font-['Poppins',sans-serif] text-xl font-semibold leading-snug tracking-tight text-slate-950 sm:text-2xl xl:text-[27px]">
          {step.title}
        </h3>

        <p className="mt-4 font-['DM_Sans',sans-serif] text-sm leading-7 text-slate-600 sm:text-[15px]">
          {step.description}
        </p>
      </div>

      {/* Checklist */}
      <div className="mt-6 border-t border-slate-100 pt-5 sm:mt-7 sm:pt-6">
        <p className="mb-4 font-['DM_Sans',sans-serif] text-xs font-bold uppercase tracking-[0.13em] text-slate-400">
          What happens
        </p>

        <ul className="space-y-3">
          {step.points.map((point) => (
            <li key={point} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
                <Check className="h-3 w-3" strokeWidth={2.5} />
              </span>

              <span className="font-['DM_Sans',sans-serif] text-sm leading-6 text-slate-600">
                {point}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Footer */}
      <div className="mt-auto flex items-center justify-between gap-3 border-t border-slate-100 pt-5 mt-7">
        <span className="font-['DM_Sans',sans-serif] text-xs font-medium leading-5 text-slate-500 sm:text-sm">
          {step.footer}
        </span>

        <MoveRight className="h-4 w-4 shrink-0 text-slate-300 transition-all duration-300 group-hover:translate-x-1 group-hover:text-indigo-600" />
      </div>
    </motion.article>
  );
}

export default function Process() {
  const sectionRef = useRef(null);

  const sectionInView = useInView(sectionRef, {
    once: true,
    amount: 0.08,
  });

  return (
    <section
      id="process"
      ref={sectionRef}
      className="relative isolate w-full overflow-hidden bg-white py-14 sm:py-16 md:py-20 lg:py-24 xl:py-28"
    >
      {/* Background decoration */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 -top-20 -z-10 h-64 w-64 rounded-full bg-indigo-100/60 blur-[100px] sm:h-80 sm:w-80 lg:h-[440px] lg:w-[440px]"
      />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-24 -left-24 -z-10 h-64 w-64 rounded-full bg-slate-100 blur-[100px] sm:h-96 sm:w-96"
      />

      {/* Full-width container */}
      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-10 xl:px-14 2xl:px-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={sectionInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="grid grid-cols-1 gap-6 sm:gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-end"
        >
          <div className="min-w-0">
            <div className="mb-5 inline-flex items-center gap-2.5 border-b-2 border-indigo-600 pb-2">
              <span className="h-2 w-2 rounded-full bg-indigo-600" />

              <span className="font-['DM_Sans',sans-serif] text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 sm:text-xs">
                Our Project Approach
              </span>
            </div>

            <h2 className="max-w-4xl font-['Poppins',sans-serif] text-3xl font-semibold leading-[1.18] tracking-tight text-slate-950 sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl 2xl:text-7xl">
              Good Spaces Don't
              <span className="block text-indigo-600">
                Happen by Accident.
              </span>
            </h2>
          </div>

          <div className="min-w-0 lg:justify-self-end lg:pb-1">
            <p className="max-w-2xl font-['DM_Sans',sans-serif] text-sm leading-7 text-slate-600 sm:text-base sm:leading-8 lg:text-lg">
              Every project deserves a thoughtful plan and careful execution.
              Our approach brings design, construction, interiors, and painting
              together through clear stages tailored to your requirements.
            </p>

            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 sm:mt-6 sm:gap-x-7">
              {["Understand", "Design", "Execute", "Handover"].map(
                (label, index) => (
                  <div key={label} className="flex items-center gap-2">
                    <span className="font-['Poppins',sans-serif] text-xs font-semibold text-indigo-600">
                      0{index + 1}
                    </span>

                    <span className="font-['DM_Sans',sans-serif] text-xs font-medium text-slate-600 sm:text-sm">
                      {label}
                    </span>
                  </div>
                )
              )}
            </div>
          </div>
        </motion.div>

        {/* Full-width process cards */}
        <div className="mt-10 grid grid-cols-1 gap-x-0 gap-y-5 sm:mt-12 sm:grid-cols-2 sm:gap-x-5 sm:gap-y-7 md:mt-14 lg:mt-16 lg:grid-cols-4 lg:gap-x-5 xl:gap-x-7 2xl:gap-x-9">
          {steps.map((step, index) => (
            <ProcessCard
              key={step.step}
              step={step}
              index={index}
              sectionInView={sectionInView}
            />
          ))}
        </div>

        {/* Closing CTA */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={sectionInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-10 flex flex-col gap-5 border-t border-slate-200 pt-7 sm:mt-12 sm:flex-row sm:items-center sm:justify-between sm:gap-8 sm:pt-8 lg:mt-16 lg:pt-10"
        >
          <div className="min-w-0">
            <p className="font-['DM_Sans',sans-serif] text-xs font-bold uppercase tracking-[0.16em] text-indigo-600 sm:text-sm">
              Let's plan your next project
            </p>

            <h3 className="mt-2 font-['Poppins',sans-serif] text-xl font-semibold leading-snug tracking-tight text-slate-950 sm:text-2xl md:text-3xl">
              Your vision deserves a thoughtful approach.
            </h3>

            <p className="mt-2 max-w-3xl font-['DM_Sans',sans-serif] text-sm leading-6 text-slate-600 sm:text-base sm:leading-7">
              Tell us what you have in mind, and let's discuss the right
              solution for your space.
            </p>
          </div>

          <a
            href="/contact"
            className="group inline-flex min-h-12 w-full shrink-0 items-center justify-center gap-3 rounded-xl bg-indigo-600 px-6 py-3.5 font-['DM_Sans',sans-serif] text-sm font-bold text-white transition-all duration-300 hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 sm:w-auto sm:min-w-[190px] sm:rounded-full sm:px-7"
          >
            Discuss Your Project

            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}